import { GENERAL_GOAL_OPTIONS, GeneralGoal, VALID_GOAL_IDS } from '@mondaygpt/constants'
import { Button } from 'components'
import { cn } from 'lib/utils'
import { Check, Clock, DollarSign, Sparkles, Star, TrendingUp, type LucideIcon } from 'lucide-react'
import { useState } from 'react'
import { Eyebrow } from './Eyebrow'
import { MobileStickyFooter } from './MobileStickyFooter'

interface GoalsStepProps {
  initial: GeneralGoal[]
  isSubmitting?: boolean
  onSubmit: (goals: GeneralGoal[]) => void
}

const MIN_GOALS = 1

/**
 * Lucide icons for each goal — overrides the emoji icons in
 * `GENERAL_GOAL_OPTIONS` so the dark/acid surface stays in voice. Adding a
 * new goal id in constants without an entry here is a TS error.
 */
const GOAL_ICONS: Record<GeneralGoal, LucideIcon> = {
  grow_audience: TrendingUp,
  save_time: Clock,
  better_content: Sparkles,
  build_brand: Star,
  increase_sales: DollarSign,
}

/**
 * Phase 4. The user picks the outcomes Taka should bias toward when
 * generating content. Pre-seeded from the scrape's `suggestedGoals`; the
 * user agrees or swaps. No cap — pick as many as you want.
 */
export const GoalsStep: React.FC<GoalsStepProps> = ({ initial, isSubmitting, onSubmit }) => {
  // Filter against current valid ids so a stale persisted goal from an older
  // progress shape (pre v5) doesn't sit invisibly in the selection.
  const [selected, setSelected] = useState<GeneralGoal[]>(() =>
    initial.filter(g => VALID_GOAL_IDS.has(g))
  )

  const toggle = (id: GeneralGoal) =>
    setSelected(prev => (prev.includes(id) ? prev.filter(g => g !== id) : [...prev, id]))

  const canContinue = selected.length >= MIN_GOALS

  return (
    <div className="mx-auto flex max-w-[600px] flex-col gap-5">
      <div className="flex flex-col gap-2.5 px-1">
        <Eyebrow>Your goals</Eyebrow>
        <h2 className="text-[32px] leading-[1.05] font-semibold tracking-[-0.02em] text-paper sm:text-[40px]">
          What are we creating for?
        </h2>
        <p className="lede max-w-[520px] !text-[15px] !leading-[1.55]">
          Pick what matters most. We'll bias what we make toward it.
        </p>
      </div>

      <div className="flex flex-col gap-2">
        {GENERAL_GOAL_OPTIONS.map(goal => (
          <GoalRow
            key={goal.id}
            Icon={GOAL_ICONS[goal.id]}
            label={goal.label}
            active={selected.includes(goal.id)}
            onClick={() => toggle(goal.id)}
          />
        ))}
      </div>

      <MobileStickyFooter>
        <Button
          variant="t3-primary"
          size="t3-lg"
          disabled={!canContinue || isSubmitting}
          aria-busy={isSubmitting}
          onClick={() => onSubmit(selected)}
          className="!max-w-[420px]"
        >
          Continue →
        </Button>
      </MobileStickyFooter>
    </div>
  )
}

const GoalRow: React.FC<{
  Icon: LucideIcon
  label: string
  active: boolean
  onClick: () => void
}> = ({ Icon, label, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    data-active={active}
    aria-pressed={active}
    className="select-card group flex w-full items-center gap-4 rounded-[10px] px-4 py-3.5 text-left"
  >
    <Icon
      className={cn(
        'h-5 w-5 shrink-0 transition-colors',
        active ? 'text-acid' : 'text-paper-dim group-hover:text-paper'
      )}
      strokeWidth={1.8}
      aria-hidden
    />
    <span className="flex-1 text-[14.5px] font-medium text-paper">{label}</span>
    <span
      className={cn(
        'flex h-5 w-5 items-center justify-center rounded-full border transition-all',
        active ? 'text-ink-on-acid border-acid bg-acid' : 'border-line bg-bg-3 text-transparent'
      )}
    >
      <Check className="h-3 w-3" strokeWidth={3} />
    </span>
  </button>
)
